import {
	FUEL_MAX,
	GRAVITY_G,
	GRAVITY_MIN_DISTANCE,
	HOMING_SEEKER_HALF_ANGLE,
	LASER_LIFETIME_TICKS,
	LASER_SPEED,
	MISSILE_COOLDOWN_TICKS,
	MISSILE_SPEED,
	SHIP_MAX_SPEED,
	SHIP_TURN_RATE,
	TURN_LEFT_SIGN,
} from "../constants";
import { type ControlState, NO_CONTROLS } from "../controls";
import type { Asteroid, Entity, Missile, Pickup, PlayerIndex, Ship } from "../entities";
import { angleDelta } from "../math/angle";
import * as DMath from "../math/det-math";
import { createRng, nextRange, type RngState } from "../math/rng";
import { wrappedAxisDelta } from "../math/torus";
import type { Vec2 } from "../math/vec2";
import type { WorldState } from "../state";
import { inZone } from "../systems/objectives";
import { findEntity } from "../world";

/** Tuning knobs that make up a difficulty level. */
export interface AiParams {
	/** Ticks between decisions (reaction time); controls are held in between. */
	thinkInterval: number;
	/** Maximum random aim offset in radians, re-rolled at every decision. */
	aimError: number;
	/** Ticks of coasting trajectory checked for planet collisions. */
	lookahead: number;
	/** Fire the laser when the nose is within this angle (radians) of the aim point. */
	fireTolerance: number;
	/** Preferred distance to the target. */
	engageRange: number;
}

/** Per-AI mutable state. Plain data: deterministic, cloneable, replayable. */
export interface AiState {
	player: PlayerIndex;
	params: AiParams;
	rng: RngState;
	/** Controls held until the next decision. */
	hold: ControlState;
	/** Tick of the next decision. */
	nextThink: number;
	aimOffset: number;
}

export function createAi(player: PlayerIndex, params: AiParams, seed: number): AiState {
	return {
		player,
		params,
		rng: createRng(seed),
		hold: { ...NO_CONTROLS },
		nextThink: 0,
		aimOffset: 0,
	};
}

/** Safety margin kept between the ship's hull and a planet's surface when predicting. */
const PLANET_MARGIN = 40;
/** Asteroids closer than this at the predicted closest approach count as threats. */
const ASTEROID_THREAT_DISTANCE = 70;
const ASTEROID_THREAT_TICKS = 90;
/** Don't accelerate towards the target faster than this. */
const CRUISE_SPEED = 5;
/** A flag carrier heading home flies flat out (the flag caps it at SHIP_MAX_SPEED × 0.9). */
const CARRIER_CRUISE_SPEED = SHIP_MAX_SPEED;
/** A carrier fights an enemy this close (e.g. a defender just respawned at its base) (I-016). */
const CARRIER_FIGHT_RANGE = 450;
/** flyTo's arrival speed: this much per tick plus 1 per this many units left, so it doesn't overshoot. */
const ARRIVAL_MIN_SPEED = 2;
const ARRIVAL_UNITS_PER_SPEED = 120;
/** Keep a fuel reserve for emergencies (fuel rule). */
const FUEL_RESERVE = 80;
/** Fire the laser within this share of its reach. */
const LASER_REACH_SHARE = 0.85;
const MISSILE_MIN_RANGE = 250;
const MISSILE_MAX_RANGE = 1200;
/** Homing missiles are worth firing from further out. */
const HOMING_MAX_RANGE = 1800;
/** Stop shooting the laser at this share of max heat, so it never locks (overheat rule). */
const HEAT_LIMIT = 0.85;
/** Detour to power-up pods within this distance when nothing threatens us. */
const POD_RANGE = 1600;
/** Capture the Flag: this close to the enemy flag, grab it instead of fighting its defender. */
const FLAG_DASH_RANGE = 1000;
/** A defender within this distance of its flag guards it: fight it first. */
const FLAG_GUARD_RANGE = 600;
/** A carrier waiting for its own flag to come home counts as at home within this distance. */
const HOME_RANGE = 500;
/** React to homing missiles chasing us within this distance. */
const HOMING_THREAT_DISTANCE = 750;

/** Returns this tick's controls for the AI's ship (re-deciding every `thinkInterval` ticks). */
export function aiControls(ai: AiState, world: WorldState): ControlState {
	if (world.tick >= ai.nextThink) {
		ai.hold = decide(ai, world);
		ai.nextThink = world.tick + ai.params.thinkInterval;
	}
	return ai.hold;
}

function decide(ai: AiState, world: WorldState): ControlState {
	const player = world.players[ai.player];
	const found = player?.shipId == null ? undefined : findEntity(world, player.shipId);
	if (found?.kind !== "ship") return { ...NO_CONTROLS };
	const ship = found;
	const { params } = ai;
	ai.aimOffset = nextRange(ai.rng, -params.aimError, params.aimError);

	// 1. Survival first: steer out of any predicted planet crash.
	const crash = predictPlanetCrash(world, ship, params.lookahead);
	if (crash) {
		const heading = escapeHeading(world, ship, crash);
		// Only burn once roughly facing the escape heading; thrusting the wrong way makes it worse.
		const facing = Math.abs(angleDelta(ship.angle, heading)) < 1.0;
		const fuelLeft = !world.rules.fuel || ship.fuel > 0;
		return steer(ship, heading, {
			thrust: facing && fuelLeft,
			fireLaser: false,
			fireMissile: false,
		});
	}

	// 2. Shake off homing missiles chasing us: cross their path (they overshoot and lose us).
	const chaser = homingThreat(world, ship);
	if (chaser) {
		const heading = evadeHeading(ship, chaser);
		const facing = Math.abs(angleDelta(ship.angle, heading)) < 1.0;
		return steer(ship, heading, {
			thrust: facing && fuelLeft(world, ship, 0),
			fireLaser: false,
			fireMissile: false,
		});
	}

	// 3. Shoot asteroids that are about to hit us; 4. grab a nearby power-up; 5. hunt.
	const threat = asteroidThreat(world, ship);
	const enemy = nearestEnemy(world, ship);
	const pod = threat ? null : usefulPod(world, ship, enemy);
	if (pod) return flyTo(world, ship, pod);
	// Objective modes (P11.2): head for the objective unless there is something to shoot.
	const goal = threat ? null : objectiveGoal(world, ship, enemy, params.engageRange);
	if (goal?.kind === "go")
		return snapShot(world, ship, enemy, ai, flyTo(world, ship, goal, goal.cruise ?? CRUISE_SPEED));
	const target = threat ?? (goal?.kind === "fight" ? goal.ship : enemy);
	if (!target)
		return steer(ship, ship.angle, { thrust: false, fireLaser: false, fireMissile: false });

	const rel = delta(world, ship.pos, target.pos);
	const dist = DMath.hypot(rel.x, rel.y);
	const aim = leadAim(
		rel,
		{ x: target.vel.x - ship.vel.x, y: target.vel.y - ship.vel.y },
		LASER_SPEED,
	);
	const aimAngle = DMath.atan2(aim.y, aim.x) + ai.aimOffset;
	const aimError = Math.abs(angleDelta(ship.angle, aimAngle));

	// Close in when too far, without overspeeding towards the target.
	const towardSpeed = ship.vel.x * DMath.cos(aimAngle) + ship.vel.y * DMath.sin(aimAngle);
	const fuelOk = !world.rules.fuel || ship.fuel > FUEL_RESERVE;
	const thrust =
		!threat && fuelOk && dist > params.engageRange && aimError < 0.6 && towardSpeed < CRUISE_SPEED;

	const reach = world.rules.laserRange ?? LASER_SPEED * LASER_LIFETIME_TICKS;
	const fireLaser =
		aimError < params.fireTolerance && dist < reach * LASER_REACH_SHARE && laserCool(world, ship);
	const missileAim = leadAim(
		rel,
		{ x: target.vel.x - ship.vel.x, y: target.vel.y - ship.vel.y },
		MISSILE_SPEED,
	);
	const missileError = Math.abs(angleDelta(ship.angle, DMath.atan2(missileAim.y, missileAim.x)));
	const fireMissile =
		!threat &&
		!fireLaser &&
		missileError < params.fireTolerance * 2 &&
		dist > MISSILE_MIN_RANGE &&
		dist < MISSILE_MAX_RANGE;

	// Homing shots only need the target inside the seeker cone; they take priority.
	const homingShot =
		!threat &&
		ship.homingShots > 0 &&
		missileReady(world, ship) &&
		missileError < HOMING_SEEKER_HALF_ANGLE * 0.6 &&
		dist > MISSILE_MIN_RANGE &&
		dist < HOMING_MAX_RANGE;
	if (homingShot) return steer(ship, aimAngle, { thrust, fireLaser: false, fireMissile: true });

	return steer(ship, aimAngle, { thrust, fireLaser, fireMissile });
}

function fuelLeft(world: WorldState, ship: Ship, reserve: number): boolean {
	return !world.rules.fuel || ship.fuel > reserve;
}

/** Overheat rule: keep the laser below the lock-out (fire at the cooling rate near the limit). */
function laserCool(world: WorldState, ship: Ship): boolean {
	const { rules } = world;
	if (!rules.laserOverheat) return true;
	if (ship.laserOverheated) return false;
	return ship.laserHeat + rules.laserHeatPerShot < rules.laserHeatMax * HEAT_LIMIT;
}

function missileReady(world: WorldState, ship: Ship): boolean {
	const stocked = world.rules.missilesPerLife === null || ship.missiles > 0;
	return stocked && ship.missileCharge >= MISSILE_COOLDOWN_TICKS;
}

/** The closest enemy homing missile locked onto us and still closing in, if any. */
function homingThreat(world: WorldState, ship: Ship): Missile | null {
	let best: Missile | null = null;
	let bestDist = HOMING_THREAT_DISTANCE;
	for (const e of world.entities) {
		if (e.kind !== "missile" || e.homingTicks <= 0 || e.targetId !== ship.id) continue;
		const rel = delta(world, e.pos, ship.pos);
		const dist = DMath.hypot(rel.x, rel.y);
		const closing = (e.vel.x - ship.vel.x) * rel.x + (e.vel.y - ship.vel.y) * rel.y > 0;
		if (closing && dist < bestDist) {
			best = e;
			bestDist = dist;
		}
	}
	return best;
}

/** Across the missile's flight path, on the side we already drift towards. */
function evadeHeading(ship: Ship, missile: Missile): number {
	const heading = DMath.atan2(missile.vel.y, missile.vel.x);
	const side = -DMath.sin(heading) * ship.vel.x + DMath.cos(heading) * ship.vel.y >= 0 ? 1 : -1;
	return heading + (side * Math.PI) / 2;
}

/** Is this pod worth picking up right now? */
function wantsPod(world: WorldState, ship: Ship, pod: Pickup): boolean {
	switch (pod.item) {
		case "fuel":
			return world.rules.fuel && ship.fuel < FUEL_MAX * 0.7;
		case "extraMissiles":
			return world.rules.missilesPerLife !== null;
		default:
			return true;
	}
}

/** The nearest wanted pod within reach, unless the enemy is closer and in range to fight. */
function usefulPod(world: WorldState, ship: Ship, enemy: Ship | null): Pickup | null {
	let best: Pickup | null = null;
	let bestDist = POD_RANGE;
	for (const e of world.entities) {
		if (e.kind !== "pickup" || !wantsPod(world, ship, e)) continue;
		const d = delta(world, ship.pos, e.pos);
		const dist = DMath.hypot(d.x, d.y);
		if (dist < bestDist) {
			best = e;
			bestDist = dist;
		}
	}
	if (!best || !enemy) return best;
	const toEnemy = delta(world, ship.pos, enemy.pos);
	return DMath.hypot(toEnemy.x, toEnemy.y) < bestDist * 0.6 ? null : best;
}

/** Flies to a static point, steering against our own drift so we arrive on it. */
/**
 * What an objective mode asks of this pilot: fly to a point (the hill, a flag, our base) or
 * fight a ship (an enemy close by, or the one carrying our flag). Null outside objective modes.
 */
function objectiveGoal(
	world: WorldState,
	ship: Ship,
	enemy: Ship | null,
	engageRange: number,
): { kind: "go"; pos: Vec2; cruise?: number } | { kind: "fight"; ship: Ship } | null {
	const objective = world.objective;
	if (!objective) return null;
	const near = (other: Ship | null, range: number) => {
		if (!other) return false;
		const d = delta(world, ship.pos, other.pos);
		return DMath.hypot(d.x, d.y) < range;
	};
	if (objective.kind === "koth") {
		if (inZone(world, objective, ship.pos)) {
			return enemy
				? { kind: "fight", ship: enemy }
				: { kind: "go", pos: zoneSpot(world, objective.zone, ship.pos) };
		}
		if (near(enemy, engageRange * 0.8) && enemy) return { kind: "fight", ship: enemy };
		return { kind: "go", pos: zoneSpot(world, objective.zone, ship.pos) };
	}
	if (objective.kind !== "ctf") return null; // score limit, 2008 survival: no objective to fly to
	const own = objective.flags[ship.player];
	const carrying = objective.flags.find((f) => f.carrier === ship.player);
	if (carrying && own) {
		// An enemy right on top of us (a defender respawning at its base): fight it, we can't outrun it.
		if (enemy && near(enemy, CARRIER_FIGHT_RANGE)) return { kind: "fight", ship: enemy };
		// Our flag is out: a capture needs it home, so return it or hunt its thief (I-016).
		if (!own.atHome && own.carrier === null) return { kind: "go", pos: own.pos };
		if (own.carrier !== null && nearHome(world, ship, own.home)) {
			const thief = shipOfPlayer(world, own.carrier);
			if (thief) return { kind: "fight", ship: thief };
		}
		// Run for home faster than we cruise: the respawned defender chases from our take (I-016).
		return { kind: "go", pos: own.home, cruise: CARRIER_CRUISE_SPEED };
	}
	if (own && own.carrier !== null) {
		const thief = shipOfPlayer(world, own.carrier);
		if (thief) return { kind: "fight", ship: thief };
	}
	if (own && !own.atHome && own.carrier === null) return { kind: "go", pos: own.pos };
	const theirs = objective.flags.find((f) => f.owner !== ship.player);
	// Close to the enemy flag while its defender is down or away: dash for it.
	const toFlag = theirs ? delta(world, ship.pos, theirs.pos) : null;
	const guard = enemy && theirs ? delta(world, enemy.pos, theirs.pos) : null;
	const unguarded = guard === null || DMath.hypot(guard.x, guard.y) > FLAG_GUARD_RANGE;
	const dash = unguarded && toFlag !== null && DMath.hypot(toFlag.x, toFlag.y) < FLAG_DASH_RANGE;
	if (!dash && near(enemy, engageRange) && enemy) return { kind: "fight", ship: enemy };
	return theirs ? { kind: "go", pos: theirs.pos } : null;
}

/**
 * On the way to an objective: fire the laser at an enemy that happens to be in our sights,
 * without turning away from the course.
 */
function snapShot(
	world: WorldState,
	ship: Ship,
	enemy: Ship | null,
	ai: AiState,
	controls: ControlState,
): ControlState {
	if (!enemy || !laserCool(world, ship)) return controls;
	const rel = delta(world, ship.pos, enemy.pos);
	const dist = DMath.hypot(rel.x, rel.y);
	const reach = world.rules.laserRange ?? LASER_SPEED * LASER_LIFETIME_TICKS;
	if (dist > reach * LASER_REACH_SHARE) return controls;
	const aim = leadAim(
		rel,
		{ x: enemy.vel.x - ship.vel.x, y: enemy.vel.y - ship.vel.y },
		LASER_SPEED,
	);
	const error = Math.abs(angleDelta(ship.angle, DMath.atan2(aim.y, aim.x)));
	return error < ai.params.fireTolerance * 2 ? { ...controls, fireLaser: true } : controls;
}

/** The middle of the hill's band, on our side of it. */
function zoneSpot(
	world: WorldState,
	zone: { x: number; y: number; inner: number; outer: number },
	from: Vec2,
): Vec2 {
	const d = delta(world, zone, from);
	const dist = DMath.hypot(d.x, d.y) || 1;
	const r = (zone.inner + zone.outer) / 2;
	return { x: zone.x + (d.x / dist) * r, y: zone.y + (d.y / dist) * r };
}

function nearHome(world: WorldState, ship: Ship, home: Vec2): boolean {
	const d = delta(world, ship.pos, home);
	return DMath.hypot(d.x, d.y) < HOME_RANGE;
}

function shipOfPlayer(world: WorldState, player: PlayerIndex): Ship | null {
	const id = world.players[player]?.shipId;
	const e = id == null ? undefined : findEntity(world, id);
	return e?.kind === "ship" ? e : null;
}

function flyTo(
	world: WorldState,
	ship: Ship,
	pod: { pos: Vec2 },
	cruise = CRUISE_SPEED,
): ControlState {
	const rel = delta(world, ship.pos, pod.pos);
	const dist = DMath.hypot(rel.x, rel.y);
	const speed = Math.min(cruise, ARRIVAL_MIN_SPEED + dist / ARRIVAL_UNITS_PER_SPEED);
	const lead = Math.min(40, dist / speed);
	const heading = DMath.atan2(rel.y - ship.vel.y * lead, rel.x - ship.vel.x * lead);
	const towardSpeed = (ship.vel.x * rel.x + ship.vel.y * rel.y) / (dist || 1);
	const aligned = Math.abs(angleDelta(ship.angle, heading)) < 0.5;
	// Arriving too fast, the corrected heading points against our motion: burn to brake, or
	// gravity carries us past a small target like a flag base (I-016).
	const braking =
		towardSpeed > speed && DMath.cos(heading) * ship.vel.x + DMath.sin(heading) * ship.vel.y < 0;
	return steer(ship, heading, {
		thrust: aligned && (towardSpeed < speed || braking) && fuelLeft(world, ship, FUEL_RESERVE),
		fireLaser: false,
		fireMissile: false,
	});
}

/** Turns towards `heading` (with a dead band so the ship doesn't jitter). */
function steer(
	ship: Ship,
	heading: number,
	rest: Pick<ControlState, "thrust" | "fireLaser" | "fireMissile">,
): ControlState {
	const d = angleDelta(ship.angle, heading);
	const deadband = SHIP_TURN_RATE / 2;
	// In y-down space "left" rotates by TURN_LEFT_SIGN (negative angles).
	const wantNegative = d < -deadband;
	const wantPositive = d > deadband;
	const left = TURN_LEFT_SIGN < 0 ? wantNegative : wantPositive;
	const right = TURN_LEFT_SIGN < 0 ? wantPositive : wantNegative;
	return { ...rest, left, right };
}

/**
 * Time for a projectile of speed `speed` (relative to the shooter) to reach a target at
 * relative position `rel` moving with relative velocity `relVel`; null if unreachable.
 */
export function interceptTime(rel: Vec2, relVel: Vec2, speed: number): number | null {
	const a = relVel.x * relVel.x + relVel.y * relVel.y - speed * speed;
	const b = 2 * (rel.x * relVel.x + rel.y * relVel.y);
	const c = rel.x * rel.x + rel.y * rel.y;
	if (Math.abs(a) < 1e-9) return b < 0 ? -c / b : null;
	const disc = b * b - 4 * a * c;
	if (disc < 0) return null;
	const sq = Math.sqrt(disc);
	const t1 = (-b - sq) / (2 * a);
	const t2 = (-b + sq) / (2 * a);
	const candidates = [t1, t2].filter((t) => t > 0);
	return candidates.length > 0 ? Math.min(...candidates) : null;
}

/** Point to aim at (relative to the shooter) to hit a moving target; falls back to direct aim. */
export function leadAim(rel: Vec2, relVel: Vec2, speed: number): Vec2 {
	const t = interceptTime(rel, relVel, speed);
	if (t === null) return rel;
	return { x: rel.x + relVel.x * t, y: rel.y + relVel.y * t };
}

interface Crash {
	planet: Entity;
	ticks: number;
}

/** Simulates the ship coasting under gravity (as the sim does) and reports the first planet hit. */
export function predictPlanetCrash(world: WorldState, ship: Ship, ticks: number): Crash | null {
	const planets = world.entities.filter((e) => e.kind === "planet");
	let x = ship.pos.x;
	let y = ship.pos.y;
	let vx = ship.vel.x;
	let vy = ship.vel.y;
	const { width, height } = world.size;
	for (let t = 1; t <= ticks; t++) {
		x += vx;
		y += vy;
		// Same single-step wrap as the sim's WrapBoundaryStrategy.
		if (x < 0) x += width;
		else if (x > width) x -= width;
		if (y < 0) y += height;
		else if (y > height) y -= height;
		for (const p of planets) {
			const dx = axis(world, x, p.pos.x, "x");
			const dy = axis(world, y, p.pos.y, "y");
			const dist = DMath.hypot(dx, dy);
			if (dist < p.radius + ship.radius + PLANET_MARGIN) return { planet: p, ticks: t };
			if (dist >= GRAVITY_MIN_DISTANCE) {
				const k = (GRAVITY_G * p.mass) / (dist * dist * dist);
				vx += dx * k;
				vy += dy * k;
			}
		}
	}
	return null;
}

/** Heading away from the planet, slanted along the current motion so the ship swings past. */
function escapeHeading(world: WorldState, ship: Ship, crash: Crash): number {
	const out = delta(world, crash.planet.pos, ship.pos);
	const len = DMath.hypot(out.x, out.y) || 1;
	const nx = out.x / len;
	const ny = out.y / len;
	// Tangent on the side the ship is already moving towards.
	const side = ship.vel.x * -ny + ship.vel.y * nx >= 0 ? 1 : -1;
	const tx = -ny * side;
	const ty = nx * side;
	const urgency = crash.ticks < 25 ? 1.5 : 1;
	return DMath.atan2(ny * urgency + ty * 0.8, nx * urgency + tx * 0.8);
}

/** The asteroid heading for a collision with us soonest, if any. */
function asteroidThreat(world: WorldState, ship: Ship): Asteroid | null {
	let best: Asteroid | null = null;
	let bestT = Number.POSITIVE_INFINITY;
	for (const e of world.entities) {
		if (e.kind !== "asteroid") continue;
		const rel = delta(world, ship.pos, e.pos);
		const rv = { x: e.vel.x - ship.vel.x, y: e.vel.y - ship.vel.y };
		const speedSq = rv.x * rv.x + rv.y * rv.y;
		if (speedSq < 1e-6) continue;
		const t = -(rel.x * rv.x + rel.y * rv.y) / speedSq; // time of closest approach
		if (t < 0 || t > ASTEROID_THREAT_TICKS) continue;
		const cx = rel.x + rv.x * t;
		const cy = rel.y + rv.y * t;
		if (DMath.hypot(cx, cy) < ASTEROID_THREAT_DISTANCE + e.radius && t < bestT) {
			best = e;
			bestT = t;
		}
	}
	return best;
}

function nearestEnemy(world: WorldState, ship: Ship): Ship | null {
	let best: Ship | null = null;
	let bestDist = Number.POSITIVE_INFINITY;
	for (const e of world.entities) {
		if (e.kind !== "ship" || e.player === ship.player) continue;
		const d = delta(world, ship.pos, e.pos);
		const dist = DMath.hypot(d.x, d.y);
		if (dist < bestDist) {
			best = e;
			bestDist = dist;
		}
	}
	return best;
}

/** Offset from `from` to `to`, wrap-aware (the shortest way round the torus). */
function delta(world: WorldState, from: Vec2, to: Vec2): Vec2 {
	return {
		x: wrappedAxisDelta(from.x, to.x, world.size.width),
		y: wrappedAxisDelta(from.y, to.y, world.size.height),
	};
}

/** Offset to a planet on one axis, matching how gravity measures it under the current rules. */
function axis(world: WorldState, from: number, to: number, which: "x" | "y"): number {
	if (!world.rules.wrapPhysics) return to - from;
	return wrappedAxisDelta(from, to, which === "x" ? world.size.width : world.size.height);
}
